/**
 * A soil channel's levels: when it reads wet, when it needs water (refill), and
 * when it is dry. The server judges every reading against them, shows the farm's
 * probes worst first, and raises a dry one in "Needs attention". Irrigation
 * admin seat to change; everyone sees them.
 */
import { useEffect, useState } from 'react';
import { Save, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import sensorDevicesService, { type MoistureLevels } from '../../services/sensorDevices';
import {
  TYPICAL_WATERMARK_LEVELS,
  levelsProblem,
  type MoistureDirection,
} from '../../utils/soilMoisture';

function num(s: string): number | null {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function str(n: number | undefined | null): string {
  return n == null ? '' : String(n);
}

export default function MoistureLevelsCard({
  deviceId,
  channelKind,
  channelIndex,
  channelLabel,
  typeKey,
  unit,
  direction,
  levels,
  canEdit,
  onSaved,
}: {
  deviceId: string;
  channelKind: string;
  channelIndex: number;
  channelLabel: string;
  typeKey: string;
  unit: string | null;
  direction: MoistureDirection;
  levels: MoistureLevels | null;
  canEdit: boolean;
  onSaved: (levels: MoistureLevels | null) => void;
}) {
  const [wet, setWet] = useState(str(levels?.wet));
  const [refill, setRefill] = useState(str(levels?.refill));
  const [dry, setDry] = useState(str(levels?.dry));
  const [staleHours, setStaleHours] = useState(str(levels?.stale_hours));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setWet(str(levels?.wet));
    setRefill(str(levels?.refill));
    setDry(str(levels?.dry));
    setStaleHours(str(levels?.stale_hours));
    setError(null);
    setSaved(false);
  }, [levels, channelKind, channelIndex]);

  const higher = direction === 'higher_is_drier';
  const u = unit ? ` (${unit})` : '';
  const problem = levelsProblem({ wet: num(wet), refill: num(refill), dry: num(dry) }, direction);

  const save = async () => {
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError(null);
    setSaved(false);
    const next: MoistureLevels = { refill: num(refill)!, dry: num(dry)! };
    const w = num(wet);
    if (w != null) next.wet = w;
    const sh = num(staleHours);
    if (sh != null) next.stale_hours = sh;
    try {
      await sensorDevicesService.setMoistureLevels(deviceId, channelKind, channelIndex, next);
      setSaved(true);
      onSaved(next);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg ?? 'Could not save the levels.');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm(`Remove the levels from ${channelLabel}? It will no longer be flagged dry.`)) return;
    setSaving(true);
    setError(null);
    try {
      await sensorDevicesService.setMoistureLevels(deviceId, channelKind, channelIndex, null);
      onSaved(null);
    } catch {
      setError('Could not remove the levels.');
    } finally {
      setSaving(false);
    }
  };

  const field = (label: string, value: string, set: (v: string) => void, hint: string) => (
    <label className="block min-w-0">
      <span className="text-xs font-medium text-gray-700">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => set(e.target.value)}
        disabled={!canEdit || saving}
        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
      />
      <span className="mt-0.5 block text-xs text-gray-500">{hint}</span>
    </label>
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          Moisture levels
          <span className="ml-2 text-xs font-normal text-gray-500">for {channelLabel}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {!levels && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            No levels are set, so this probe is never flagged dry.
            {canEdit ? ' Set them below.' : ' An Irrigation Admin can set them.'}
          </p>
        )}
        <p className="text-sm text-gray-600">
          {higher
            ? 'Soil tension rises as the soil dries. A reading at or above refill needs water; at or above dry, the block is dry.'
            : 'Soil water falls as the soil dries. A reading at or below refill needs water; at or below dry, the block is dry.'}
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {field(`Wet${u}`, wet, setWet, higher ? 'At or below: wet. Optional.' : 'At or above: wet. Optional.')}
          {field(`Refill${u}`, refill, setRefill, 'Time to water.')}
          {field(`Dry${u}`, dry, setDry, 'Raised in Needs attention.')}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {field('Quiet after (hours)', staleHours, setStaleHours, 'Flag the probe if it has not reported for this long. Blank is 6.')}
        </div>
        {typeKey === 'soil_tension' && canEdit && (
          <p className="text-xs text-gray-500">
            <button
              type="button"
              onClick={() => {
                setWet(str(TYPICAL_WATERMARK_LEVELS.wet));
                setRefill(str(TYPICAL_WATERMARK_LEVELS.refill));
                setDry(str(TYPICAL_WATERMARK_LEVELS.dry));
              }}
              className="font-semibold text-blue-600 hover:text-blue-800"
            >
              Start from typical Watermark levels
            </button>{' '}
            (10 / 40 / 60 kPa). The right levels depend on the soil, the crop and the probe&apos;s depth; check them with your agronomist.
          </p>
        )}
        {(error || (problem && (refill || dry))) && (
          <p className="text-sm text-red-600">{error ?? problem}</p>
        )}
        {saved && !error && <p className="text-sm text-green-700">Levels saved.</p>}
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={save} disabled={saving || Boolean(problem)}>
              <Save className="mr-1 h-4 w-4" /> {saving ? 'Saving…' : 'Save levels'}
            </Button>
            {levels && (
              <Button size="sm" variant="outline" onClick={clear} disabled={saving}>
                <Trash2 className="mr-1 h-4 w-4" /> Remove levels
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
